import { apiFetch } from "../api/client";
import { useTenantScope } from "../scope/TenantScopeContext";
import React, { useCallback, useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL || "https://api.wirelesspress.org";

function storedProfile() {
  for (const key of ["user", "currentUser", "profile"]) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || "null");
      if (value && typeof value === "object") return value;
    } catch (_) {}
  }
  return {};
}

function authHeaders(json = false) {
  const token =
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    "";
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(json ? { "Content-Type": "application/json" } : {}),
  };
}

async function apiJson(path, options = {}) {
  const response = await apiFetch(`${path}`, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.detail || `Request failed with HTTP ${response.status}`);
  }
  return body;
}

const panel = {
  background: "#101923",
  border: "1px solid #263747",
  borderRadius: 14,
  padding: 20,
  marginBottom: 18,
};

const input = {
  width: "100%",
  boxSizing: "border-box",
  background: "#0a1118",
  color: "#eef5fb",
  border: "1px solid #31485b",
  borderRadius: 8,
  padding: "10px 12px",
};

const button = {
  border: 0,
  borderRadius: 8,
  padding: "9px 13px",
  color: "white",
  background: "#b42318",
  cursor: "pointer",
  fontWeight: 700,
};

export default function RemoteUninstallPage() {
  const globalScope = useTenantScope();
  const profile = useMemo(storedProfile, []);
  const role = profile.role || localStorage.getItem("role") || "";

  const companyId =
    role === "super_admin"
      ? String(globalScope.companyId || "")
      : String(
          profile.company_id ||
            localStorage.getItem("company_id") ||
            ""
        );
  const [devices, setDevices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const requestQuery =
    role === "super_admin" && companyId
      ? `?company_id=${companyId}`
      : "";

  const devicePath =
    companyId
      ? `/devices/company/${companyId}`
      : "";

  const load = useCallback(async () => {
    setError("");

    if (role === "super_admin" && !companyId) {
      setDevices([]);
      setRequests([]);
      return;
    }

    try {
      const [deviceRows, requestRows] = await Promise.all([
        apiJson(devicePath, { headers: authHeaders() }),
        apiJson(`/remote-uninstall/requests${requestQuery}`, {
          headers: authHeaders(),
        }),
      ]);
      setDevices(Array.isArray(deviceRows) ? deviceRows : []);
      setRequests(Array.isArray(requestRows) ? requestRows : []);
    } catch (err) {
      setError(err.message);
    }
  }, [companyId, devicePath, requestQuery, role]);

  useEffect(() => {
    load();
  }, [load]);

  async function mutate(path, payload) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const body = {
        ...payload,
        ...(role === "super_admin" && companyId && payload.company_id == null
          ? { company_id: Number(companyId) }
          : {}),
      };
      const result = await apiJson(path, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify(body),
      });
      setNotice(`Request ${result.request_uuid || ""} is ${result.status}.`);
      await load();
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function createRequest(event) {
    event.preventDefault();
    if (!deviceId || !reason.trim()) return;
    const selectedDevice = devices.find(
      (device) => String(device.id) === String(deviceId)
    );
    const result = await mutate("/remote-uninstall/requests", {
      device_id: Number(deviceId),
      reason: reason.trim(),
      approval_ttl_minutes: 60,
      ...(role === "super_admin" && selectedDevice?.company_id
        ? { company_id: Number(selectedDevice.company_id) }
        : {}),
    });
    if (result) {
      setReason("");
      setDeviceId("");
    }
  }

  return (
    <main style={{ color: "#eef5fb", padding: 24, maxWidth: 1240, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 6 }}>Remote Uninstall</h1>
      <p style={{ color: "#a9bbca", marginTop: 0 }}>
        Two-person approval, tenant isolation, authenticated endpoint execution, and complete audit history.
      </p>

      <div style={{ ...panel, borderColor: "#8a6418", background: "#211b0d" }}>
        <strong>Remote uninstall control:</strong> The server approval and authenticated agent-delivery workflow is active.
        This dashboard exposes uninstall requests only for active Windows endpoints. Execution still depends on the capabilities of the installed endpoint agent.
        Successful uninstall preserves endpoint identity for recovery or reinstallation.
      </div>

      {error && <div style={{ ...panel, borderColor: "#b42318", color: "#ffb4ab" }}>{error}</div>}
      {notice && <div style={{ ...panel, borderColor: "#16815d", color: "#9ce4c8" }}>{notice}</div>}

      <section style={panel}>
        <h2>Request uninstall</h2>
        <form onSubmit={createRequest} style={{ display: "grid", gap: 12 }}>
          <label>
            Active device
            <select value={deviceId} onChange={(event) => setDeviceId(event.target.value)} style={input}>
              <option value="">Select a device</option>
              {devices
                .filter(
                  (device) =>
                    (device.lifecycle_state || "active") === "active" &&
                    String(device.platform || "")
                      .toLowerCase()
                      .includes("windows")
                )
                .map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.hostname || device.device_name || device.device_uuid} — {device.device_uuid}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Business and security reason
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              maxLength={500}
              style={input}
              placeholder="Explain why this managed endpoint must be uninstalled."
            />
          </label>
          <button disabled={busy || !deviceId || !reason.trim()} style={{ ...button, width: "fit-content" }}>
            Request second approval
          </button>
        </form>
      </section>

      <section style={panel}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2>Approval and execution history</h2>
          <button onClick={load} disabled={busy} style={{ ...button, background: "#285d7a" }}>Refresh</button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {[
                  "Device", "Status", "Requested by", "Approved by", "Identity", "Created", "Actions",
                ].map((name) => (
                  <th key={name} style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #31485b" }}>{name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((item) => (
                <tr key={item.request_uuid}>
                  <td style={{ padding: 10 }}>{item.device_uuid}</td>
                  <td style={{ padding: 10 }}>{item.status}</td>
                  <td style={{ padding: 10 }}>{item.requested_by}</td>
                  <td style={{ padding: 10 }}>{item.approved_by || "Awaiting second admin"}</td>
                  <td style={{ padding: 10 }}>{item.local_identity_policy}</td>
                  <td style={{ padding: 10 }}>{item.created_at}</td>
                  <td style={{ padding: 10, display: "flex", gap: 8 }}>
                    {item.status === "pending_second_approval" && (
                      <button
                        disabled={busy}
                        style={{ ...button, background: "#16815d" }}
                        onClick={() => mutate(
                          `/remote-uninstall/requests/${item.request_uuid}/approve`,
                          role === "super_admin" ? { company_id: item.company_id } : {}
                        )}
                      >
                        Approve
                      </button>
                    )}
                    {["pending_second_approval", "approved", "dispatched"].includes(item.status) && (
                      <button
                        disabled={busy}
                        style={{ ...button, background: "#5b6570" }}
                        onClick={() => mutate(
                          `/remote-uninstall/requests/${item.request_uuid}/cancel`,
                          role === "super_admin" ? { company_id: item.company_id } : {}
                        )}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!requests.length && (
                <tr><td colSpan="7" style={{ padding: 18, color: "#a9bbca" }}>No remote uninstall requests.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
